// src/pages/UserManagement.jsx

import React, { useEffect, useState } from "react";
import { deleteApp, initializeApp } from "firebase/app";
import {
  addDoc,
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  orderBy,
  query,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { auth, db, firebaseConfig } from "../firebase";
import {
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  LogOut,
  Shield,
  Trash2,
  CheckCircle,
  XCircle,
  UserPlus,
} from "lucide-react";

/* ================= CACHE SETTINGS ================= */
const CACHE_KEYS = {
  USERS: "usermanagement_users_cache",
  CACHE_TIME: "usermanagement_cache_time",
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const QUERY_LIMIT = 100;
const TITLE_LOGO_SRC = "/uklogo.png";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [agentForm, setAgentForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    openingBalance: "0",
    paymentNote: "MoMo payment sent directly to admin",
    momoSenderNumber: "",
    momoReference: "",
    momoAmountPaid: "",
    momoPaidConfirmed: false,
  });
  const SUPER_ADMIN_EMAIL = "admin@admin.com";


  const usersPerPage = 10;
  const navigate = useNavigate();

  const clearUsersCache = () => {
    localStorage.removeItem(CACHE_KEYS.USERS);
    localStorage.removeItem(CACHE_KEYS.CACHE_TIME);
  };

  /* ================= AUTH (ADMIN ONLY) ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/agent/login");
        return;
      }

      const snap = await getDocs(query(collection(db, "users")));
      const me = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .find(
          (u) =>
            u.email?.toLowerCase() === user.email?.toLowerCase()
        );

      if (!me || me.role !== "admin" || me.approved !== true) {
        alert("Admins only.");
        await signOut(auth);
        navigate("/agent/login");
      }
    });

    return unsub;
  }, [navigate]);

  /* ================= LOAD USERS ================= */
  const loadUsers = async () => {
    setLoading(true);
    
    // Check cache first
    const cachedTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
    if (cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION) {
      const cached = localStorage.getItem(CACHE_KEYS.USERS);
      if (cached) {
        setUsers(JSON.parse(cached));
        setLoading(false);
        return;
      }
    }
    
    // Fetch with limit
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc"),
      limit(QUERY_LIMIT)
    );
    const snap = await getDocs(q);
    const userData = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(
        (u) =>
          u.email?.toLowerCase() !== SUPER_ADMIN_EMAIL.toLowerCase()
      );
    
    setUsers(userData);
    
    // Cache the results
    localStorage.setItem(CACHE_KEYS.USERS, JSON.stringify(userData));
    localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());

    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  /* ================= ACTIONS ================= */

  // Cycle role: user → agent → admin → user
  const toggleRole = async (id, role) => {
    const next =
      role === "user" ? "agent" : role === "agent" ? "admin" : "user";

    if (!window.confirm(`Change role to ${next}?`)) return;

    await updateDoc(doc(db, "users", id), {
      role: next,
      approved: next === "admin" ? false : true,
    });

    clearUsersCache();
    loadUsers();
  };

  // Approve / revoke admin
  const toggleApproval = async (id, approved) => {
    await updateDoc(doc(db, "users", id), {
      approved: !approved,
    });
    clearUsersCache();
    loadUsers();
  };

  const toggleAgentPaid = async (userRecord) => {
    if (userRecord.role !== "agent") {
      return;
    }

    const nextPaid = !userRecord.paid;
    const message = nextPaid
      ? "Mark this agent as paid?"
      : "Mark this agent as not paid?";

    if (!window.confirm(message)) {
      return;
    }

    await updateDoc(doc(db, "users", userRecord.id), {
      paid: nextPaid,
      updatedAt: serverTimestamp(),
    });

    clearUsersCache();
    loadUsers();
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await deleteDoc(doc(db, "users", id));
    clearUsersCache();
    setUsers((p) => p.filter((u) => u.id !== id));
  };

  const creditWallet = async (userRecord) => {
    const rawAmount = window.prompt("Enter wallet amount to credit (GHS):", "0");
    if (rawAmount === null) return;

    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Enter a valid wallet amount.");
      return;
    }

    const nextBalance = Number((Number(userRecord.walletBalance || 0) + amount).toFixed(2));

    await updateDoc(doc(db, "users", userRecord.id), {
      walletBalance: nextBalance,
      updatedAt: serverTimestamp(),
    });

    await addDoc(collection(db, "walletTransactions"), {
      agentId: userRecord.id,
      agentEmail: userRecord.email,
      amount,
      balanceBefore: Number(userRecord.walletBalance || 0),
      balanceAfter: nextBalance,
      type: "admin_credit",
      creditedBy: auth.currentUser?.email || "admin",
      createdAt: serverTimestamp(),
    });

    clearUsersCache();
    alert(`Wallet credited successfully. New balance: GHS ${nextBalance.toFixed(2)}`);
    loadUsers();
  };

  const createAgentManually = async (event) => {
    event.preventDefault();

    const firstName = agentForm.firstName.trim();
    const lastName = agentForm.lastName.trim();
    const email = agentForm.email.trim().toLowerCase();
    const phone = agentForm.phone.trim();
    const password = agentForm.password.trim();
    const openingBalance = Number(agentForm.openingBalance || 0);
    const paymentNote = agentForm.paymentNote.trim() || "MoMo payment sent directly to admin";
    const momoSenderNumber = agentForm.momoSenderNumber.trim();
    const momoReference = agentForm.momoReference.trim();
    const momoAmountPaid = Number(agentForm.momoAmountPaid || 0);
    const momoPaidConfirmed = Boolean(agentForm.momoPaidConfirmed);

    if (!firstName || !lastName || !email || !phone || !password) {
      alert("Complete all agent fields.");
      return;
    }

    if (!/^0\d{9}$/.test(phone)) {
      alert("Phone number must be 10 digits and start with 0.");
      return;
    }

    if (password.length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }

    if (!Number.isFinite(openingBalance) || openingBalance < 0) {
      alert("Opening wallet balance must be zero or more.");
      return;
    }

    if (!momoPaidConfirmed) {
      alert("Confirm direct MoMo payment before creating the agent.");
      return;
    }

    if (!/^0\d{9}$/.test(momoSenderNumber)) {
      alert("MoMo sender number must be 10 digits and start with 0.");
      return;
    }

    if (!momoReference) {
      alert("Enter MoMo payment reference.");
      return;
    }

    if (!Number.isFinite(momoAmountPaid) || momoAmountPaid <= 0) {
      alert("Enter a valid MoMo amount paid.");
      return;
    }

    setCreatingAgent(true);

    const tempAppName = `admin-create-agent-${Date.now()}`;
    const tempApp = initializeApp(firebaseConfig, tempAppName);
    const tempAuth = getAuth(tempApp);

    try {
      const cred = await createUserWithEmailAndPassword(tempAuth, email, password);
      const fullName = `${firstName} ${lastName}`.trim();

      await setDoc(doc(db, "users", cred.user.uid), {
        email,
        role: "agent",
        approved: true,
        paid: true,
        firstName,
        lastName,
        fullName,
        businessName: fullName,
        phone,
        walletBalance: Number(openingBalance.toFixed(2)),
        onboardingFee: Number(momoAmountPaid.toFixed(2)),
        onboardingReference: momoReference,
        onboardingMethod: "momo_direct_to_admin",
        onboardingPaidByNumber: momoSenderNumber,
        paymentStatus: "verified",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
        paymentNote,
      });

      await addDoc(collection(db, "walletTransactions"), {
        agentId: cred.user.uid,
        agentEmail: email,
        amount: Number(openingBalance.toFixed(2)),
        balanceBefore: 0,
        balanceAfter: Number(openingBalance.toFixed(2)),
        type: "admin_manual_setup",
        method: "momo_direct_to_admin",
        status: "completed",
        momoAmountPaid: Number(momoAmountPaid.toFixed(2)),
        momoSenderNumber,
        momoReference,
        note: paymentNote,
        createdBy: auth.currentUser?.email || "admin",
        createdAt: serverTimestamp(),
      });

      clearUsersCache();
      await loadUsers();
      setAgentForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        openingBalance: "0",
        paymentNote: "MoMo payment sent directly to admin",
        momoSenderNumber: "",
        momoReference: "",
        momoAmountPaid: "",
        momoPaidConfirmed: false,
      });
      alert("Agent account created successfully.");
    } catch (createError) {
      alert(createError.message || "Unable to create agent account.");
    } finally {
      await signOut(tempAuth).catch(() => undefined);
      await deleteApp(tempApp).catch(() => undefined);
      setCreatingAgent(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/agent/login");
  };

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(users.length / usersPerPage) || 1;
  const pageUsers = users.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-slate-800 text-white p-4 pb-24">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center"
        >
          <h1 className="text-3xl font-extrabold text-sky-400 flex items-center gap-2">
            <img src={TITLE_LOGO_SRC} alt="UK Connect logo" className="h-9 w-auto max-w-[150px] object-contain" />
            <Shield /> User Management
          </h1>

          <div className="hidden md:flex gap-3">
            <NavBtn icon={<LayoutDashboard />} label="Dashboard" link="/admin/dashboard" />
            <NavBtn icon={<CreditCard />} label="Txns" link="/admin/transactions" />
            <NavBtn icon={<LogOut />} danger onClick={handleLogout} />
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={createAgentManually}
          className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-xl"
        >
          <div className="mb-4 flex items-center gap-2 text-sky-300">
            <UserPlus size={18} />
            <h2 className="text-lg font-bold">Add Agent After Direct MoMo Payment</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <input
              type="text"
              placeholder="First name"
              value={agentForm.firstName}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, firstName: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="text"
              placeholder="Last name"
              value={agentForm.lastName}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, lastName: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="email"
              placeholder="Email"
              value={agentForm.email}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, email: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="tel"
              placeholder="Phone e.g. 024xxxxxxx"
              value={agentForm.phone}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, phone: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="password"
              placeholder="Temporary password"
              value={agentForm.password}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, password: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Opening wallet balance"
              value={agentForm.openingBalance}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, openingBalance: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="text"
              placeholder="Payment note"
              value={agentForm.paymentNote}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, paymentNote: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none xl:col-span-2"
            />
            <input
              type="tel"
              placeholder="MoMo sender number"
              value={agentForm.momoSenderNumber}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, momoSenderNumber: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="text"
              placeholder="MoMo reference"
              value={agentForm.momoReference}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, momoReference: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="MoMo amount paid"
              value={agentForm.momoAmountPaid}
              onChange={(event) => setAgentForm((prev) => ({ ...prev, momoAmountPaid: event.target.value }))}
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none"
            />
            <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm xl:col-span-2">
              <input
                type="checkbox"
                checked={agentForm.momoPaidConfirmed}
                onChange={(event) =>
                  setAgentForm((prev) => ({ ...prev, momoPaidConfirmed: event.target.checked }))
                }
              />
              I confirm this agent has paid via MoMo directly to admin.
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-300">
              Creates the auth account only after direct MoMo payment is confirmed, then marks the agent as paid and optionally credits the wallet.
            </p>
            <button
              type="submit"
              disabled={creatingAgent}
              className="rounded-xl bg-sky-600 px-4 py-3 font-bold text-white disabled:opacity-60"
            >
              {creatingAgent ? "Creating Agent..." : "Create Agent"}
            </button>
          </div>
        </motion.form>

        {/* TABLE */}
        <motion.div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                <th className="p-3 text-left">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Approved</th>
                <th className="p-3">Paid</th>
                <th className="p-3">Wallet</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              <AnimatePresence>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-6 text-center">
                      Loading…
                    </td>
                  </tr>
                ) : (
                  pageUsers.map((u) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="p-3">{u.email}</td>

                      <td className="p-3 text-center">
                        <span className="px-3 py-1 rounded-full text-xs bg-sky-500/20 text-sky-400">
                          {u.role}
                        </span>
                      </td>

                      <td className="p-3 text-center">
                        {u.role === "admin" ? (
                          u.approved ? (
                            <CheckCircle className="inline text-green-400" />
                          ) : (
                            <XCircle className="inline text-red-400" />
                          )
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-3 text-center">
                        {u.role === "agent"
                          ? u.paid
                            ? "Yes"
                            : "No"
                          : "-"}
                      </td>

                      <td className="p-3 text-center font-semibold text-emerald-400">
                        {u.role === "agent" ? `GHS ${Number(u.walletBalance || 0).toFixed(2)}` : "-"}
                      </td>

                      <td className="p-3 flex justify-end gap-2">
                        <button
                          onClick={() => toggleRole(u.id, u.role)}
                          className="px-2 py-1 bg-yellow-500 rounded text-xs"
                        >
                          Role
                        </button>

                        {u.role === "admin" && (
                          <button
                            onClick={() =>
                              toggleApproval(u.id, u.approved)
                            }
                            className="px-2 py-1 bg-green-600 rounded text-xs"
                          >
                            {u.approved ? "Revoke" : "Approve"}
                          </button>
                        )}

                        {u.role === "agent" && (
                          <button
                            onClick={() => toggleAgentPaid(u)}
                            className={`px-2 py-1 rounded text-xs ${u.paid ? "bg-amber-600" : "bg-green-600"}`}
                          >
                            {u.paid ? "Mark Unpaid" : "Mark Paid"}
                          </button>
                        )}

                        {u.role === "agent" && (
                          <button
                            onClick={() => creditWallet(u)}
                            className="px-2 py-1 bg-emerald-600 rounded text-xs"
                          >
                            Credit Wallet
                          </button>
                        )}

                        <button
                          onClick={() => deleteUser(u.id)}
                          className="px-2 py-1 bg-red-600 rounded text-xs flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </motion.div>

        {/* PAGINATION */}
        <div className="flex justify-center gap-4">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-4 py-2 bg-gray-700 rounded disabled:opacity-40"
          >
            ◀
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 bg-gray-700 rounded disabled:opacity-40"
          >
            ▶
          </button>
        </div>
      </div>

      {/* MOBILE NAV */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-black/90 border-t border-white/10 flex justify-around py-3">
        <MobileNav icon={<LayoutDashboard />} label="Home" link="/admin/dashboard" />
        <MobileNav icon={<Users />} label="Users" />
        <MobileNav icon={<CreditCard />} label="Txns" link="/admin/transactions" />
        <button onClick={handleLogout} className="flex flex-col items-center text-red-400">
          <LogOut />
          <span className="text-xs">Logout</span>
        </button>
      </div>
    </div>
  );
}

/* ================= UI HELPERS ================= */

const NavBtn = ({ icon, label, link, onClick, danger }) => (
  <button
    onClick={() => (link ? (window.location.href = link) : onClick())}
    className={`px-4 py-3 rounded-xl font-bold flex items-center gap-2 ${
      danger ? "bg-red-600" : "bg-sky-600"
    }`}
  >
    {icon}
    {label}
  </button>
);

const MobileNav = ({ icon, label, link }) => (
  <button
    onClick={() => link && (window.location.href = link)}
    className="flex flex-col items-center text-gray-300"
  >
    {icon}
    <span className="text-xs">{label}</span>
  </button>
);
